import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  VirtualConnectionTableUpstreamVirtualConnectionsFilteredByColumnQuery,
  VirtualConnectionTableUpstreamVirtualConnectionsFilteredByColumnQuery_virtualConnectionTables_columnsConnection_nodes_upstreamVirtualConnectionsConnection_nodes,
  VirtualConnectionTableUpstreamVirtualConnectionsFilteredByColumnQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionTableUpstreamVirtualConnectionsFilteredByColumnQuery';
import {
  VirtualConnectionTableUpstreamVirtualConnectionsQuery,
  VirtualConnectionTableUpstreamVirtualConnectionsQuery_virtualConnectionTables_upstreamVirtualConnectionsConnection_nodes,
  VirtualConnectionTableUpstreamVirtualConnectionsQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionTableUpstreamVirtualConnectionsQuery';
import { VirtualConnectionsDisplayGridRowDataFragment } from '../../../gen-src/queries/VirtualConnectionsDisplayGridRowDataFragment';
import { VirtualConnectionSortOrder } from '../../../gen-src/queries/globalTypes';
import { VirtualConnectionTableUpstreamVirtualConnectionsFilteredByColumnQueryGql } from '../../queries/VirtualConnectionTableUpstreamVirtualConnectionsFilteredByColumnQueryGql';
import { VirtualConnectionTableUpstreamVirtualConnectionsQueryGql } from '../../queries/VirtualConnectionTableUpstreamVirtualConnectionsQueryGql';
import { getRowDataWithMaxCountsForVirtualConnection } from '../database/functions/VirtualConnectionRowDataFunctions';
import { VirtualConnectionsDisplayGridRowData } from '../database/types/VirtualConnectionDisplayGridRowData';
import { VirtualConnectionsDisplayGrid } from '../sharedDisplayGrid/VirtualConnectionsDisplayGrid';
import { VirtualConnectionsValidSortFields } from '../sorting/sortProviders/VirtualConnectionsGridSortProvider';


type QueryVariables = VirtualConnectionTableUpstreamVirtualConnectionsQueryVariables;
type QueryResults = VirtualConnectionTableUpstreamVirtualConnectionsQuery;
type QueryResultListNode = VirtualConnectionTableUpstreamVirtualConnectionsQuery_virtualConnectionTables_upstreamVirtualConnectionsConnection_nodes;
type GridRowData = VirtualConnectionsDisplayGridRowData;

type QueryVariablesWithFilter = VirtualConnectionTableUpstreamVirtualConnectionsFilteredByColumnQueryVariables;
type QueryResultsWithFilter = VirtualConnectionTableUpstreamVirtualConnectionsFilteredByColumnQuery;
type QueryResultListNodeWithFilter =
  VirtualConnectionTableUpstreamVirtualConnectionsFilteredByColumnQuery_virtualConnectionTables_columnsConnection_nodes_upstreamVirtualConnectionsConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.virtualConnectionTables && resultData.virtualConnectionTables[0] &&
    resultData.virtualConnectionTables[0].upstreamVirtualConnectionsConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const virtualConnection = resultData && resultData.virtualConnectionTables && resultData.virtualConnectionTables[0];
  const virtualConnectionTableColumns = virtualConnection && virtualConnection.columnsConnection && virtualConnection.columnsConnection.nodes;
  const column = virtualConnectionTableColumns && virtualConnectionTableColumns[0];
  return column ? column.upstreamVirtualConnectionsConnection : null;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData {
  return resultData && resultData.virtualConnectionTables && resultData.virtualConnectionTables[0];
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData | null {
  const virtualConnection = resultData && resultData.virtualConnectionTables && resultData.virtualConnectionTables[0];
  const virtualConnectionTableColumns = virtualConnection && virtualConnection.columnsConnection && virtualConnection.columnsConnection.nodes;
  const column = virtualConnectionTableColumns && virtualConnectionTableColumns[0];
  return column;
}

function getRowDataForVirtualConnection(
  table: VirtualConnectionsDisplayGridRowDataFragment,
  resultData: QueryResults,
): VirtualConnectionsDisplayGridRowData {
  return getRowDataWithMaxCountsForVirtualConnection(table, selectMaxCountsResult(resultData));
}

function getRowDataForVirtualConnectionForFilteredQuery(
  table: VirtualConnectionsDisplayGridRowDataFragment,
  resultData: QueryResultsWithFilter,
): VirtualConnectionsDisplayGridRowData {
  return getRowDataWithMaxCountsForVirtualConnection(table, selectMaxCountsResultForFilteredQuery(resultData));
}

export type VirtualConnectionTableUpstreamVirtualConnectionsQueryGridProps = GridSortProps<VirtualConnectionsValidSortFields> & {
  readonly vizportalUrlId: string;
  readonly filteredColumnId: string | null;
  readonly rowCount: number | null;
};

export function VirtualConnectionTableUpstreamVirtualConnectionsQueryGrid(props: VirtualConnectionTableUpstreamVirtualConnectionsQueryGridProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);

  const emptyGridMessage = Messages.EmptyUpstreamVirtualConnectionsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredVirtualConnectionsLineageGridMessage();

  return props.filteredColumnId ? (
    // Query with Selected VirtualConnection as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionTableUpstreamVirtualConnectionsFilteredByColumnQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        selectedColumnId: props.filteredColumnId,
        orderBy: props.orderBy as VirtualConnectionSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForVirtualConnectionForFilteredQuery}
    >
      {({ rowStore }) =>
        <VirtualConnectionsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionTableUpstreamVirtualConnectionsQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        orderBy: props.orderBy as VirtualConnectionSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForVirtualConnection}
    >
      {({ rowStore }) =>
        <VirtualConnectionsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
    </PaginatedQueryWrapper>
  );
}
